import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { busAPI, seatTemplateAPI, vehicleTypeAPI } from '../services/api';
import VehicleTypeBadge from '../components/VehicleTypeBadge';

const API_HOST = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');

const AMENITIES = [
  { name: 'hasAC',       label: 'تكييف',      icon: '❄️',  color: 'bg-blue-100 text-blue-600'    },
  { name: 'hasWifi',     label: 'واي فاي',    icon: '📶', color: 'bg-indigo-100 text-indigo-600' },
  { name: 'hasScreens',  label: 'شاشات',      icon: '📺', color: 'bg-purple-100 text-purple-600' },
  { name: 'hasChargers', label: 'مقابس شحن',  icon: '🔌', color: 'bg-yellow-100 text-yellow-600' },
];

const SEAT_CONFIGS = [
  { value: '2+2', label: '2+2 — قياسي (4 في الصف)' },
  { value: '2+1', label: '2+1 — VIP (3 في الصف)'   },
  { value: '1+1', label: '1+1 — ميني باص (2 في الصف)' },
  { value: 'van', label: 'فان — حافلة صغيرة (تخطيط ثابت 11-15 مقعد)' },
];

const INPUT_CLS = 'w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400';
const LABEL_CLS = 'block text-sm font-medium text-gray-700 mb-1';

// ── TemplateForm ───────────────────────────────────────────────────────────────
const TemplateForm = ({ initial, vehicleTypes, onSave, onCancel, saving }) => {
  const [form, setForm] = useState({
    name:        initial?.name        || '',
    config:      initial?.config      || '2+2',
    totalSeats:  initial?.totalSeats  || '',
    backRowSize: initial?.backRowSize ?? 5,
    description: initial?.description || '',
    vehicleTypeId: initial?.vehicleTypeId || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.totalSeats) {
      toast.error('الاسم وعدد المقاعد مطلوبان');
      return;
    }
    onSave({
      name:        form.name.trim(),
      config:      form.config,
      totalSeats:  parseInt(form.totalSeats),
      backRowSize: parseInt(form.backRowSize) || 5,
      description: form.description.trim() || null,
      vehicleTypeId: form.vehicleTypeId || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={LABEL_CLS}>اسم القالب *</label>
          <input
            value={form.name}
            onChange={e => set('name', e.target.value)}
            className={INPUT_CLS}
            placeholder="مثال: قياسي 52 مقعد 2+2"
            required
          />
        </div>
        <div>
          <label className={LABEL_CLS}>تكوين المقاعد *</label>
          <select
            value={form.config}
            onChange={e => set('config', e.target.value)}
            className={INPUT_CLS}
          >
            {SEAT_CONFIGS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className={LABEL_CLS}>إجمالي المقاعد *</label>
          <input
            type="number"
            value={form.totalSeats}
            onChange={e => set('totalSeats', e.target.value)}
            className={INPUT_CLS}
            placeholder="52"
            min={1}
            required
          />
        </div>
        <div>
          <label className={LABEL_CLS}>مقاعد الصف الخلفي</label>
          <input
            type="number"
            value={form.backRowSize}
            onChange={e => set('backRowSize', e.target.value)}
            className={INPUT_CLS}
            min={1}
            max={10}
          />
          <p className="text-xs text-gray-400 mt-1">عادةً 5 مقاعد في الصف الخلفي الكامل العرض</p>
        </div>
        <div>
          <label className={LABEL_CLS}>ملاحظات</label>
          <input
            value={form.description}
            onChange={e => set('description', e.target.value)}
            className={INPUT_CLS}
            placeholder="وصف اختياري"
          />
        </div>
        <div>
          <label className={LABEL_CLS}>نوع المركبة</label>
          <select
            value={form.vehicleTypeId}
            onChange={e => set('vehicleTypeId', e.target.value)}
            className={INPUT_CLS}
          >
            <option value="">— غير مرتبط —</option>
            {(vehicleTypes || []).map(v => (
              <option key={v.id} value={v.id}>{v.icon} {v.name}</option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-1">يُستخدم لتصفية القوالب عند اختيار نوع المركبة في نموذج الباص</p>
        </div>
      </div>
      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
        >
          إلغاء
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 rounded-xl text-sm font-semibold transition"
        >
          {saving ? 'جاري الحفظ...' : 'حفظ القالب'}
        </button>
      </div>
    </form>
  );
};

// ── BusForm ───────────────────────────────────────────────────────────────────
const BusForm = ({ initial, templates, vehicleTypes, onSave, onCancel, saving }) => {
  const [form, setForm] = useState({
    plateNumber:  initial?.plateNumber  || '',
    manufacturer: initial?.manufacturer || '',
    model:        initial?.model        || '',
    year:         initial?.year         || '',
    totalSeats:   initial?.totalSeats   || '',
    seatConfig:   initial?.seatLayout?.config || '2+2',
    templateId:   initial?.seatTemplateId || '',
    vehicleTypeId: initial?.vehicleTypeId || '',
    hasAC:        initial?.hasAC        || false,
    hasWifi:      initial?.hasWifi      || false,
    hasScreens:   initial?.hasScreens   || false,
    hasChargers:  initial?.hasChargers  || false,
  });
  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState(
    initial?.layoutImageUrl
      ? (initial.layoutImageUrl.startsWith('http') ? initial.layoutImageUrl : `${API_HOST}${initial.layoutImageUrl}`)
      : null
  );
  const [removeImage, setRemoveImage] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectedTemplate = templates.find(t => t.id === form.templateId) || null;

  // Templates tagged for the chosen vehicle type; fall back to showing all
  // templates when none are tagged yet so bus creation is never blocked.
  const filteredTemplates = form.vehicleTypeId
    ? (templates.some(t => t.vehicleTypeId === form.vehicleTypeId)
        ? templates.filter(t => t.vehicleTypeId === form.vehicleTypeId || !t.vehicleTypeId)
        : templates)
    : templates;

  const handleTemplateChange = (tid) => {
    const tpl = templates.find(t => t.id === tid);
    if (tpl) {
      setForm(f => ({
        ...f,
        templateId: tid,
        totalSeats: String(tpl.totalSeats),
        seatConfig: tpl.config,
        vehicleTypeId: f.vehicleTypeId || tpl.vehicleTypeId || '',
      }));
    } else {
      setForm(f => ({ ...f, templateId: '' }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setRemoveImage(false);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(true);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.plateNumber || !form.manufacturer || !form.model) {
      toast.error('يرجى ملء الحقول المطلوبة');
      return;
    }
    if (!form.templateId && !form.totalSeats) {
      toast.error('يرجى تحديد قالب مقاعد أو إدخال عدد المقاعد يدوياً');
      return;
    }

    const fd = new FormData();
    fd.append('plateNumber',  form.plateNumber);
    fd.append('manufacturer', form.manufacturer);
    fd.append('model',        form.model);
    fd.append('year',         form.year);
    fd.append('hasAC',        form.hasAC);
    fd.append('hasWifi',      form.hasWifi);
    fd.append('hasScreens',   form.hasScreens);
    fd.append('hasChargers',  form.hasChargers);
    fd.append('vehicleTypeId', form.vehicleTypeId || '');

    if (form.templateId) {
      fd.append('seatTemplateId', form.templateId);
      // totalSeats and seatLayout are derived on the backend from the template
    } else {
      fd.append('totalSeats', form.totalSeats);
      const perRow = form.seatConfig === 'van' ? 3 : form.seatConfig === '2+1' ? 3 : form.seatConfig === '1+1' ? 2 : 4;
      fd.append('seatLayout', JSON.stringify({
        config:     form.seatConfig,
        rows:       Math.ceil(parseInt(form.totalSeats || 1) / perRow),
        seatsPerRow: perRow,
        ...(form.seatConfig === 'van' && { backRowSize: Math.max(0, Math.min(4, parseInt(form.totalSeats || 11) - 11)) }),
      }));
    }

    if (imageFile)   fd.append('layoutImage', imageFile);
    if (removeImage) fd.append('removeLayoutImage', 'true');
    onSave(fd);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">

      {/* Vehicle type picker */}
      <div>
        <label className={LABEL_CLS}>نوع المركبة</label>
        <select
          value={form.vehicleTypeId}
          onChange={e => set('vehicleTypeId', e.target.value)}
          className={INPUT_CLS}
        >
          <option value="">— غير محدد —</option>
          {vehicleTypes.map(v => (
            <option key={v.id} value={v.id}>{v.icon} {v.name} ({v.minCapacity}-{v.maxCapacity} مقعد)</option>
          ))}
        </select>
      </div>

      {/* Template picker */}
      <div className="bg-indigo-50 rounded-xl p-4 space-y-2">
        <label className="block text-sm font-semibold text-indigo-800 mb-1">قالب المقاعد</label>
        <select
          value={form.templateId}
          onChange={e => handleTemplateChange(e.target.value)}
          className="w-full border border-indigo-200 bg-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
        >
          <option value="">— بدون قالب (إدخال يدوي) —</option>
          {filteredTemplates.map(t => (
            <option key={t.id} value={t.id}>
              {t.name}  ({t.config} | {t.totalSeats} مقعد)
            </option>
          ))}
        </select>
        {selectedTemplate && (
          <div className="flex flex-wrap gap-2 pt-1">
            <span className="bg-indigo-100 text-indigo-700 text-xs px-2.5 py-0.5 rounded-full font-semibold">
              {selectedTemplate.config}
            </span>
            <span className="bg-indigo-100 text-indigo-700 text-xs px-2.5 py-0.5 rounded-full">
              {selectedTemplate.totalSeats} مقعد
            </span>
            <span className="bg-indigo-100 text-indigo-700 text-xs px-2.5 py-0.5 rounded-full">
              صف خلفي: {selectedTemplate.backRowSize}
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={LABEL_CLS}>رقم اللوحة *</label>
          <input
            value={form.plateNumber}
            onChange={e => set('plateNumber', e.target.value)}
            className={INPUT_CLS}
            placeholder="AKL-1234"
            required
          />
        </div>
        <div>
          <label className={LABEL_CLS}>الشركة المصنعة *</label>
          <input
            value={form.manufacturer}
            onChange={e => set('manufacturer', e.target.value)}
            className={INPUT_CLS}
            placeholder="مرسيدس"
            required
          />
        </div>
        <div>
          <label className={LABEL_CLS}>الموديل *</label>
          <input
            value={form.model}
            onChange={e => set('model', e.target.value)}
            className={INPUT_CLS}
            placeholder="Tourismo"
            required
          />
        </div>
        <div>
          <label className={LABEL_CLS}>السنة</label>
          <input
            type="number"
            value={form.year}
            onChange={e => set('year', e.target.value)}
            className={INPUT_CLS}
            placeholder="2022"
          />
        </div>

        {/* Seats + config — manual only when no template selected */}
        {!form.templateId ? (
          <>
            <div>
              <label className={LABEL_CLS}>عدد المقاعد *</label>
              <input
                type="number"
                value={form.totalSeats}
                onChange={e => set('totalSeats', e.target.value)}
                className={INPUT_CLS}
                placeholder="45"
                min={1}
                required
              />
            </div>
            <div>
              <label className={LABEL_CLS}>تكوين المقاعد *</label>
              <select
                value={form.seatConfig}
                onChange={e => set('seatConfig', e.target.value)}
                className={INPUT_CLS}
              >
                {SEAT_CONFIGS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <p className="text-xs text-gray-400 mt-1">يحدد شكل مخطط المقاعد في واجهة الحجز</p>
            </div>
          </>
        ) : (
          <div className="sm:col-span-2 bg-gray-50 rounded-xl px-4 py-2.5 text-sm text-gray-500">
            المقاعد والتكوين مشتقان تلقائياً من القالب المحدد
          </div>
        )}

        <div className="sm:col-span-2">
          <label className={LABEL_CLS}>المرافق</label>
          <div className="flex flex-wrap gap-3 pt-1">
            {AMENITIES.map(a => (
              <label key={a.name} className="flex items-center gap-1.5 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!form[a.name]}
                  onChange={e => set(a.name, e.target.checked)}
                  className="rounded"
                />
                <span>{a.icon} {a.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Layout image */}
      <div>
        <label className={LABEL_CLS}>
          صورة تخطيط المقاعد
          <span className="text-gray-400 font-normal mr-1">(اختياري)</span>
        </label>
        <div className="space-y-2">
          {imagePreview && (
            <div className="relative inline-block">
              <img
                src={imagePreview}
                alt="تخطيط المقاعد"
                className="w-full max-w-xs h-36 object-contain rounded-xl border border-gray-200 bg-gray-50"
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute top-1 left-1 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs transition"
              >✕</button>
            </div>
          )}
          <label className={`flex items-center gap-3 cursor-pointer border-2 border-dashed rounded-xl px-4 py-3 transition
            ${imagePreview ? 'border-gray-200 hover:border-blue-300' : 'border-blue-200 hover:border-blue-400 bg-blue-50/30'}`}>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png"
              className="hidden"
              onChange={handleImageChange}
            />
            <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-sm text-gray-500">
              {imagePreview ? 'تغيير الصورة' : 'انقر لرفع صورة تخطيط الباص'}
            </span>
            <span className="text-xs text-gray-400 mr-auto">JPG, PNG — حد أقصى 5 ميغا</span>
          </label>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
        >
          إلغاء
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-semibold transition"
        >
          {saving ? 'جاري الحفظ...' : 'حفظ'}
        </button>
      </div>
    </form>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const FleetPage = () => {
  const [buses,        setBuses]        = useState([]);
  const [templates,    setTemplates]    = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showAdd,   setShowAdd]   = useState(false);
  const [editBus,   setEditBus]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [layoutModal, setLayoutModal] = useState(null);

  // Template form state
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editTemplate,     setEditTemplate]     = useState(null);
  const [templateSaving,   setTemplateSaving]   = useState(false);

  const fetchBuses = () => {
    busAPI.getAll()
      .then(res => setBuses(res.data.data || []))
      .catch(() => toast.error('فشل في تحميل الأسطول'))
      .finally(() => setLoading(false));
  };

  const fetchTemplates = () => {
    seatTemplateAPI.getAll()
      .then(res => setTemplates(res.data.data || []))
      .catch(() => {});
  };

  const fetchVehicleTypes = () => {
    vehicleTypeAPI.getAll()
      .then(res => setVehicleTypes(res.data.data || []))
      .catch(() => {});
  };

  useEffect(() => { fetchBuses(); fetchTemplates(); fetchVehicleTypes(); }, []);

  // ── Bus handlers ─────────────────────────────────────────────────────────────
  const handleAdd = async (fd) => {
    setSaving(true);
    try {
      await busAPI.create(fd);
      toast.success('تم إضافة الباص بنجاح');
      setShowAdd(false);
      fetchBuses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل في إضافة الباص');
    } finally { setSaving(false); }
  };

  const handleEdit = async (fd) => {
    setSaving(true);
    try {
      await busAPI.update(editBus.id, fd);
      toast.success('تم تحديث بيانات الباص');
      setEditBus(null);
      fetchBuses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل في تحديث الباص');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل تريد إلغاء تفعيل هذا الباص؟')) return;
    try {
      await busAPI.delete(id);
      toast.success('تم إلغاء تفعيل الباص');
      fetchBuses();
    } catch { toast.error('فشل في إلغاء التفعيل'); }
  };

  // ── Template handlers ─────────────────────────────────────────────────────────
  const handleAddTemplate = async (data) => {
    setTemplateSaving(true);
    try {
      await seatTemplateAPI.create(data);
      toast.success('تم إضافة القالب');
      setShowTemplateForm(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل في إضافة القالب');
    } finally { setTemplateSaving(false); }
  };

  const handleEditTemplate = async (data) => {
    setTemplateSaving(true);
    try {
      await seatTemplateAPI.update(editTemplate.id, data);
      toast.success('تم تحديث القالب');
      setEditTemplate(null);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل في تحديث القالب');
    } finally { setTemplateSaving(false); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('هل تريد حذف هذا القالب؟ لن يؤثر ذلك على الباصات المرتبطة به.')) return;
    try {
      await seatTemplateAPI.delete(id);
      toast.success('تم حذف القالب');
      fetchTemplates();
    } catch { toast.error('فشل في حذف القالب'); }
  };

  const resolveImg = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_HOST}${url}`;
  };

  const activeTemplates = templates.filter(t => t.isActive !== false);

  return (
    <div dir="rtl" className="p-4 md:p-6 space-y-6">

      {/* Layout image preview modal */}
      {layoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setLayoutModal(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-800">تخطيط مقاعد الباص</h3>
              <p className="text-xs text-gray-400">{layoutModal.manufacturer} {layoutModal.model}</p>
              <button onClick={() => setLayoutModal(null)} className="p-1.5 rounded-xl hover:bg-gray-100 text-gray-400">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <img
              src={resolveImg(layoutModal.layoutImageUrl)}
              alt="تخطيط المقاعد"
              className="w-full rounded-xl border object-contain max-h-[70vh]"
            />
          </div>
        </div>
      )}

      {/* Edit Bus modal */}
      {editBus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => !saving && setEditBus(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-5">تعديل بيانات الباص</h2>
            <BusForm
              initial={editBus}
              templates={activeTemplates}
              vehicleTypes={vehicleTypes}
              onSave={handleEdit}
              onCancel={() => setEditBus(null)}
              saving={saving}
            />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">إدارة الأسطول</h1>
        <button
          onClick={() => { setShowAdd(!showAdd); setEditBus(null); }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl transition text-sm font-medium whitespace-nowrap"
        >
          {showAdd ? 'إلغاء' : '+ إضافة باص'}
        </button>
      </div>

      {/* ── Seat Templates section ─────────────────────────────────────────────── */}
      <section className="bg-white rounded-2xl shadow p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-bold text-gray-800">قوالب المقاعد</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              حدد تكوين الباص مرة واحدة — يُطبَّق تلقائياً عند إضافة الباص
            </p>
          </div>
          {!showTemplateForm && !editTemplate && (
            <button
              onClick={() => setShowTemplateForm(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-xl transition text-sm font-medium"
            >
              + إضافة قالب
            </button>
          )}
        </div>

        {/* Template add/edit inline form */}
        {(showTemplateForm || editTemplate) && (
          <div className="mb-5 p-4 bg-gray-50 border border-gray-200 rounded-xl">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              {editTemplate ? 'تعديل القالب' : 'قالب جديد'}
            </h3>
            <TemplateForm
              initial={editTemplate}
              vehicleTypes={vehicleTypes}
              onSave={editTemplate ? handleEditTemplate : handleAddTemplate}
              onCancel={() => { setShowTemplateForm(false); setEditTemplate(null); }}
              saving={templateSaving}
            />
          </div>
        )}

        {/* Template list */}
        {activeTemplates.length === 0 ? (
          <div className="text-center py-6 text-gray-400">
            <div className="text-3xl mb-2">🗂️</div>
            <p className="text-sm">لا توجد قوالب مقاعد.</p>
            <p className="text-xs mt-1">أضف قالباً لتسهيل إنشاء الباصات بالتكوين الصحيح.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {activeTemplates.map(t => (
              <div key={t.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <span className="text-indigo-600 font-bold text-xs">{t.config}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{t.name}</p>
                  <p className="text-xs text-gray-400">
                    {t.totalSeats} مقعد
                    {' · '}صف خلفي: {t.backRowSize}
                    {t.description ? ` · ${t.description}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setEditTemplate(t); setShowTemplateForm(false); }}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                    title="تعديل"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(t.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="حذف"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Add Bus form ──────────────────────────────────────────────────────────── */}
      {showAdd && (
        <div className="bg-white rounded-2xl shadow p-6">
          <h2 className="text-lg font-semibold mb-4">بيانات الباص الجديد</h2>
          <BusForm
            templates={activeTemplates}
            vehicleTypes={vehicleTypes}
            onSave={handleAdd}
            onCancel={() => setShowAdd(false)}
            saving={saving}
          />
        </div>
      )}

      {/* ── Bus grid ──────────────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="text-center text-gray-400 py-12">جاري التحميل...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {buses.map(bus => (
            <div key={bus.id} className="bg-white rounded-2xl shadow overflow-hidden">

              {bus.layoutImageUrl ? (
                <button
                  onClick={() => setLayoutModal(bus)}
                  className="block w-full h-32 overflow-hidden bg-gray-50 hover:bg-gray-100 transition relative group"
                >
                  <img
                    src={resolveImg(bus.layoutImageUrl)}
                    alt="تخطيط"
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <span className="bg-white/90 text-gray-700 text-xs font-medium px-3 py-1 rounded-full">عرض التخطيط</span>
                  </div>
                </button>
              ) : (
                <div className="h-20 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                  <span className="text-4xl opacity-40">🚌</span>
                </div>
              )}

              <div className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-gray-800">{bus.manufacturer} {bus.model}</h3>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${bus.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                    {bus.isActive ? 'نشط' : 'موقوف'}
                  </span>
                </div>

                {bus.vehicleType && <VehicleTypeBadge vehicleType={bus.vehicleType} />}

                <div className="text-sm text-gray-500 space-y-0.5">
                  <p>لوحة: <span className="font-medium text-gray-700">{bus.plateNumber}</span></p>
                  <p>
                    المقاعد: <span className="font-medium text-gray-700">{bus.totalSeats}</span>
                    {bus.seatLayout?.config && (
                      <span className="mr-2 text-[10px] bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded font-semibold">
                        {bus.seatLayout.config}
                      </span>
                    )}
                    {bus.year && <span className="mr-2 text-gray-400">سنة {bus.year}</span>}
                  </p>
                  {/* Show which template this bus uses */}
                  {bus.seatTemplate && (
                    <p className="flex items-center gap-1">
                      <span className="text-xs text-indigo-500">🗂️ {bus.seatTemplate.name}</span>
                    </p>
                  )}
                </div>

                {(bus.hasAC || bus.hasWifi || bus.hasScreens || bus.hasChargers) && (
                  <div className="flex flex-wrap gap-1.5">
                    {AMENITIES.filter(a => bus[a.name]).map(a => (
                      <span key={a.name} className={`${a.color} text-xs px-2 py-0.5 rounded-lg font-medium`}>
                        {a.icon} {a.label}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                  <button
                    onClick={() => { setEditBus(bus); setShowAdd(false); }}
                    className="flex-1 flex items-center justify-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 py-1.5 rounded-lg transition font-medium"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    تعديل
                  </button>
                  {bus.isActive && (
                    <button
                      onClick={() => handleDelete(bus.id)}
                      className="flex-1 flex items-center justify-center gap-1.5 text-sm text-red-500 hover:text-red-700 hover:bg-red-50 py-1.5 rounded-lg transition"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                      إلغاء التفعيل
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {buses.length === 0 && (
            <div className="col-span-3 text-center text-gray-400 py-16 bg-white rounded-2xl shadow">
              <div className="text-5xl mb-3">🚌</div>
              <p>لا توجد بصات مسجلة</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FleetPage;
